import type {ReactElement} from 'react';
import type {RGBA} from '@Utils/color.util';
import {rgbaToHexString} from '@Utils/color.util';
import {PMW_COLORS_NEUTRAL} from '@Utils/color.util';
import {getMixedColorSwatchThumbUrl} from './color-fill.util';
import styles from './color-fill.module.scss';

interface ColorFillProps {
  id?: string;
  color: RGBA | RGBA[];
  className?: string;
  height?: number | string;
  width?: number | string;
  hasFocus?: boolean;
  colorFillPropertyType?: ColorFillPropertyType;
  isDisabled?: boolean;
  isSelected?: boolean;
  isTransparent?: boolean;
  borderRadius?: number | string;
  /** When true, the swatch shows the "mixed colours" thumbnail instead of a colour. */
  isColorMixed?: boolean;
  testId?: string;

  onClick?(id?: string): void;
}

export enum ColorFillPropertyType {
  SOLID = 'SOLID',
  RADIAL = 'RADIAL',
  LINEAR = 'LINEAR',
}

const DEFAULT_COLOR_FILL_WIDTH = 50;
const DEFAULT_COLOR_FILL_HEIGHT = 30;

export function ColorFill({
  id = '',
  className = '',
  height = DEFAULT_COLOR_FILL_HEIGHT,
  width = DEFAULT_COLOR_FILL_WIDTH,
  hasFocus = true,
  onClick = (): void => {},
  colorFillPropertyType = ColorFillPropertyType.SOLID,
  isDisabled = false,
  isSelected = false,
  isTransparent = false,
  borderRadius,
  ...props
}: ColorFillProps): ReactElement<any> | null {
  const isArray = (): boolean => {
    return Array.isArray(props.color);
  };

  const mixedColorSwatchUrl = props.isColorMixed ? getMixedColorSwatchThumbUrl() : undefined;

  const getBackground = (): string => {
    if (isDisabled) {
      return PMW_COLORS_NEUTRAL.NEUTRAL_11;
    }
    if (mixedColorSwatchUrl) {
      // Position/size folded into the shorthand so the style object stays all-shorthand (mixing the
      // `background` shorthand with `backgroundPosition`/`backgroundSize` longhands warns in React).
      return `url(${mixedColorSwatchUrl}) center / cover`;
    }
    switch (colorFillPropertyType) {
      case ColorFillPropertyType.SOLID:
        return rgbaToHexString(props.color as RGBA);
      case ColorFillPropertyType.LINEAR:
        if (isArray()) {
          const color1 = rgbaToHexString(props.color[1] as RGBA);
          const color2 = rgbaToHexString(props.color[0] as RGBA);
          return `linear-gradient(${color1}, ${color2})`;
        }
        return '';

      case ColorFillPropertyType.RADIAL:
        if (isArray()) {
          const color1 = rgbaToHexString(props.color[1] as RGBA);
          const color2 = rgbaToHexString(props.color[0] as RGBA);
          return `radial-gradient(${color1}, ${color2})`;
        }
        return '';

      default:
        return '';
    }
  };

  return (
    <span
      onClick={(): void => {
        if (isDisabled) {
          return;
        }
        onClick(id);
      }}
      id={id}
      data-testid={props.testId}
      style={{
        background: isTransparent ? undefined : getBackground(),
        borderRadius,
        width,
        height,
      }}
      className={`${className} ${styles.container} ${isTransparent ? styles.transparent : ''} ${isDisabled ? styles.disabled : ''} ${isSelected ? styles.selected : ''} ${hasFocus && !isDisabled ? '' : styles.noFocus}`}
    />
  );
}
